import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLoder from '../../components/PageLoader';
import '../../styles/ConsultantEvaluation.css';
import LogoImage from '../../assets/logo_img.png';
import exportRenewalToPDF from '../../components/ExportRenewalPDF';

export default function ViewAdminRenewal() {
     const { id } = useParams();
     const navigate = useNavigate();
     // Use configured API base or fallback. Keep protocol slashes intact and trim trailing slash.
     const baseUrl = (process.env.REACT_APP_BASE_URL || 'http://localhost/int-app/api').replace(/\/$/, '');

     const [loading, setLoading] = useState(true);
     const [renewal, setRenewal] = useState(null);
     const [details, setDetails] = useState([]);
     const [message, setMessage] = useState(null);

     useEffect(() => {
          let mounted = true;
          const load = async () => {
               setLoading(true);
               try {
                    const token = localStorage.getItem('token');
                    const headers = { Accept: 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;
                    const url = `${baseUrl.replace(/\/$/, '')}/getConsultantRenewal/${id}`;
                    const res = await fetch(url, { method: 'GET', headers });
                    const json = await res.json();
                    if (!mounted) return;
                    if (json && json.renewal) {
                         setRenewal(json.renewal);
                         setDetails(Array.isArray(json.details) ? json.details : []);
                         // merge question text if missing by fetching question bank
                         try {
                              const qUrl = `${baseUrl.replace(/\/$/, '')}/getConsultantRenewalQuestions`;
                              const qres = await fetch(qUrl, { method: 'GET', headers });
                              const qjson = await qres.json();
                              if (Array.isArray(qjson)) {
                                   const map = qjson.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.question_text || q.question || '' }), {});
                                   setDetails(prev => (Array.isArray(prev) ? prev.map(d => ({ ...d, question_text: d.question_text || map[String(d.question_id)] || map[String(d.id)] || '' })) : prev));
                              }
                         } catch (err) {
                              console.debug('Failed to fetch questions for admin view:', err);
                         }
                    } else if (Array.isArray(json) && json.length > 0) {
                         setRenewal(json[0]);
                         setDetails(Array.isArray(json[0].details) ? json[0].details : []);
                         // also attempt to merge question text for array response
                         try {
                              const qUrl = `${baseUrl.replace(/\/$/, '')}/getConsultantRenewalQuestions`;
                              const qres = await fetch(qUrl, { method: 'GET', headers });
                              const qjson = await qres.json();
                              if (Array.isArray(qjson)) {
                                   const map = qjson.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.question_text || q.question || '' }), {});
                                   setDetails(prev => (Array.isArray(prev) ? prev.map(d => ({ ...d, question_text: d.question_text || map[String(d.question_id)] || map[String(d.id)] || '' })) : prev));
                              }
                         } catch (err) {
                              console.debug('Failed to fetch questions for admin view (array):', err);
                         }
                    } else if (json && typeof json === 'object') {
                         setRenewal(json);
                         setDetails(Array.isArray(json.details) ? json.details : []);
                         // merge question text for object-shaped response
                         try {
                              const qUrl = `${baseUrl.replace(/\/$/, '')}/getConsultantRenewalQuestions`;
                              const qres = await fetch(qUrl, { method: 'GET', headers });
                              const qjson = await qres.json();
                              if (Array.isArray(qjson)) {
                                   const map = qjson.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.question_text || q.question || '' }), {});
                                   setDetails(prev => (Array.isArray(prev) ? prev.map(d => ({ ...d, question_text: d.question_text || map[String(d.question_id)] || map[String(d.id)] || '' })) : prev));
                              }
                         } catch (err) {
                              console.debug('Failed to fetch questions for admin view (object):', err);
                         }
                    } else {
                         setRenewal(null);
                         setDetails([]);
                    }
               } catch (err) {
                    console.error(err);
                    setMessage({ type: 'error', text: 'Failed to load renewal' });
               } finally {
                    if (mounted) setLoading(false);
               }
          };
          load();
          return () => { mounted = false; };
     }, [baseUrl, id]);

     const handleExport = async () => {
          try {
               await exportRenewalToPDF({ renewal, details, id, baseUrl, publicUrl: process.env.PUBLIC_URL || '', logoImage: LogoImage, envFileUrl: process.env.REACT_APP_FILE_URL || '' });
          } catch (e) {
               console.debug('Export failed', e);
               setMessage({ type: 'error', text: 'Export failed' });
          }
     };

     if (loading) return <div className="text-center py-4"><PageLoder /></div>;
     if (!renewal) return <div className="consultant-eval">No renewal found.</div>;

     const formatDateTime = (s) => {
          if (!s) return null;
          try {
               let ds = String(s).trim();
               if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(ds)) ds = ds.replace(' ', 'T');
               const d = new Date(ds);
               if (!isNaN(d.getTime())) {
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const hh = String(d.getHours()).padStart(2, '0');
                    const min = String(d.getMinutes()).padStart(2, '0');
                    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
               }
          } catch (e) {
               // ignore
          }
          return s;
     };

     const computeVdc = (dojStr) => {
          if (!dojStr) return '';
          let ds = String(dojStr).trim();
          let d = null;
          if (/^\d{4}-\d{2}-\d{2}/.test(ds)) {
               d = new Date(ds.split(' ')[0]);
          } else if (/^\d{2}-\d{2}-\d{4}$/.test(ds)) {
               const [dd, mm, yyyy] = ds.split('-');
               d = new Date(`${yyyy}-${mm}-${dd}`);
          } else {
               const tmp = new Date(ds);
               if (!isNaN(tmp.getTime())) d = tmp;
          }
          if (!d || isNaN(d.getTime())) return '';
          const now = new Date();
          const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
          if (months < 0) return '';
          const yrs = Math.floor(months / 12);
          const rem = months % 12;
          const parts = [];
          if (yrs > 0) parts.push(`${yrs} year${yrs > 1 ? 's' : ''}`);
          if (rem > 0) parts.push(`${rem} month${rem > 1 ? 's' : ''}`);
          return parts.length > 0 ? parts.join(' ') : '0 months';
     };

     const renderMaybeEmpty = (v) => {
          if (v === null || v === undefined) return '';
          const s = String(v).trim();
          if (s === '') return '';
          // treat numeric zero as empty
          const n = Number(s);
          if (!isNaN(n) && n === 0) return '';
          return v;
     };

     const submittedOnRaw = renewal && (renewal.submitted_on || renewal.submitted_at || renewal.submitted_date || renewal.updated_at || renewal.updated_dtm || renewal.created_at || renewal.created_dtm || '');
     const submittedOn = formatDateTime(submittedOnRaw);

     const createdOnRaw = renewal && (renewal.created_at || renewal.created_dtm || renewal.created_on || renewal.createdDate || '');
     const createdOn = formatDateTime(createdOnRaw);
     const updatedOnRaw = renewal && (renewal.updated_at || renewal.updated_dtm || renewal.updated_on || renewal.updatedDate || '');
     const updatedOn = formatDateTime(updatedOnRaw);

     const displayedSubmittedOn = submittedOn || (renewal && renewal.is_submitted === 'Y' ? (updatedOn || createdOn) : null);

     const displayAdminTotal = (renewal && (renewal.total_admin_points !== undefined && renewal.total_admin_points !== null))
          ? renewal.total_admin_points
          : (Array.isArray(details) ? details.reduce((s, d) => s + (parseInt(d.admin_points, 10) || 0), 0) : 0);

     // consultant self total points
     const displayConsultantTotal = (renewal && (renewal.total_points !== undefined && renewal.total_points !== null))
          ? renewal.total_points
          : (Array.isArray(details) ? details.reduce((s, d) => s + (parseInt(d.points, 10) || 0), 0) : 0);

     return (
          <div className="consultant-eval">
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>View Consultant Renewal</h2>
                    <div>
                         {renewal?.is_submitted === 'Y' && (
                              <button className="btn btn-sm btn-primary" onClick={() => navigate(`/admin-evaluations/${id}/edit`)} style={{ marginRight: 8 }}>HOD Edit</button>
                         )}
                         <button className="btn btn-sm" onClick={() => navigate(`/admin-renewals`)} style={{ marginRight: 8 }}>List</button>
                         <button className="btn btn-sm" onClick={handleExport} style={{ marginLeft: 8 }}>Export PDF</button>
                    </div>
               </div>

               {message && (
                    <div style={{ marginTop: 12 }}>
                         <div style={{ padding: 10, border: '1px solid #e0b4b4', background: '#fff6f6', color: '#9f3a38' }}>{message.text}</div>
                    </div>
               )}

               <div style={{ marginTop: 12 }}>
                    <strong>Consultant:</strong> {renewal.emp_name || renewal.comp_name || ''} ({renewal.emp_code || ''})
                    {/* Specialty if empty hide */}
                    {renewal.specialty && (
                         <span style={{ marginLeft: 16 }}><strong>Specialty:</strong> {renewal.specialty}</span>
                    )}
                    {/* mobile  */}
                    <span style={{ marginLeft: 16 }}><strong>Mobile:</strong> {renewal.mobile || 'N/A'}</span>
                    {/* Employeement type */}
                    <span style={{ marginLeft: 16 }}><strong>Employment Type:</strong> {renewal.employment_type || 'N/A'}</span>

                    {/* Timing if empty hide */}
                    {renewal.timing && (
                         <span style={{ marginLeft: 16 }}><strong>Timing:</strong> {renewal.timing}</span>
                    )}
                    <span style={{ marginLeft: 16 }}><strong>DOJ:</strong> {(renewal.doj && renewal.doj !== '0000-00-00') ? renewal.doj : ''}</span>
                    <span style={{ marginLeft: 16 }}><strong>VDC Exp:</strong> {renewal.vdc_exp || computeVdc(renewal.doj) || ''}</span>
                    {/* { Department} */}
                    <span style={{ marginLeft: 16 }}><strong>Department:</strong> {renewal.dept || 'N/A'}</span>
                    <div style={{ marginTop: 8 }}>
                         <strong>Quarter:</strong> {renewal.quarter || ''}
                         <span style={{ marginLeft: 16 }}><strong>Year:</strong> {renewal.year || ''}</span>
                         <span style={{ marginLeft: 16 }}><strong>Centre:</strong> {renewal.centre || ''}</span>
                         <span style={{ marginLeft: 16 }}><strong>Submitted:</strong> {renewal.is_submitted === 'Y' ? 'Yes' : 'No'}{displayedSubmittedOn ? `, on ${displayedSubmittedOn}` : ''}</span>
                         {createdOn && (
                              <span style={{ marginLeft: 16 }}><strong>Created on:</strong> {createdOn}</span>
                         )}
                         {updatedOn && (
                              <span style={{ marginLeft: 16 }}><strong>Updated on:</strong> {updatedOn}</span>
                         )}
                         {/* HOD total points, with light background */}

                         <span style={{ marginLeft: 16, background: '#b1dcdf', padding: 4 }}><strong>Total HOD Points:</strong> {renderMaybeEmpty(displayAdminTotal)}</span>
                         {/* consultant self total points */}
                         <span style={{ marginLeft: 16, background: '#86b2f3', padding: 4 }}><strong>Total Consultant Points:</strong> {renderMaybeEmpty(displayConsultantTotal)}</span>

                    </div>
                    {/* overall comment moved below the questions table and shown in a single row */}
               </div>

               <table className="eval-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                    <thead>
                         <tr>
                              <th style={{ width: 60 }}>Sr. No</th>
                              <th>Task</th>
                              <th style={{ width: 140 }}>Consultant Points</th>
                              <th style={{ width: 140 }}>HOD Points</th>
                              <th style={{ width: 240 }}>Consultant's Comments</th>
                              <th style={{ width: 240 }}>HOD's Comments</th>
                         </tr>
                    </thead>
                    <tbody>
                         {(!details || details.length === 0) && <tr><td colSpan={6}>No tasks/details available.</td></tr>}
                         {details && details.map((d, idx) => (
                              <tr key={d.id || d.question_id || idx}>
                                   <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                   <td>{d.question_text || d.question || d.question_label || d.question_id}</td>
                                   <td>{(d.points !== undefined && d.points !== null) ? d.points : ''}</td>
                                   <td>{renderMaybeEmpty(d.admin_points)}</td>
                                   <td>{d.comments || ''}</td>
                                   <td>{d.admin_comments || ''}</td>
                              </tr>
                         ))}
                    </tbody>
               </table>
               {renewal.overall_comment && (
                    <div style={{ marginTop: 12, padding: 8, background: '#fafafa', border: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 8 }}>
                         <strong style={{ margin: 0 }}>Overall Comment:</strong>
                         <div style={{ margin: 0, flex: '1 1 auto', wordBreak: 'break-word' }}>{renewal.overall_comment}</div>
                    </div>
               )}
          </div>
     );
}
